/* Red Raven: Halloween Sales page (sections/rr-halloween-hero.liquid)
   Hero: countdown, spider pendulum physics, ghost "boo", pause when offscreen.
   Page: witch flight right-to-left on first scroll and back left-to-right over the first product rows,
   a hand that walks around the page selector. */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const pad = (n) => String(n).padStart(2, '0');

  const TRAIL_COLORS = ['#ffd27a', '#ff8a2a', '#ff6a13', '#d9c2ff', '#fff4e6'];
  function makeSprite(color) {
    const s = 32;
    const c = document.createElement('canvas');
    c.width = c.height = s;
    const g = c.getContext('2d');
    const grad = g.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
    grad.addColorStop(0, '#ffffff');
    grad.addColorStop(0.18, color);
    grad.addColorStop(1, 'rgba(0,0,0,0)');
    g.fillStyle = grad;
    g.fillRect(0, 0, s, s);
    return c;
  }

  const mainGrid = () => document.querySelector('.collection product-list.product-list, .collection .product-list');

  class HalloweenPage {
    constructor(root) {
      this.root = root;
      this.section = root.closest('.shopify-section') || root.parentElement;
      this.reduced = reduceMotion.matches;
      this.visible = true;
      this.raf = 0;
      this.last = 0;
      this.p = -1;
      this.mx = 0; this.my = 0; this.tmx = 0; this.tmy = 0;
      this.scrollVel = 0;
      this.lastScrollY = window.scrollY;
      this.flight = null;
      this.queued = null;
      this.particles = [];
      this.startedAt = performance.now();
      this.cleanups = [];
      this.observers = [];
      this.ready = false;

      this.initCountdown();
      this.initCta();
      this.setOffset();
      const ready = this.whenReady();
      if (this.reduced) { ready.then(() => this.reveal()); return; }

      this.initSpiders();
      this.initGhost();
      this.initFly();
      this.initHand();
      this.measure();
      this.watchBackFlight();

      this.on(window, 'resize', () => this.measure(), { passive: true });
      this.on(window, 'scroll', () => this.onScroll(), { passive: true });
      this.on(window, 'wheel', () => this.firstFlight(), { passive: true });
      this.on(window, 'touchmove', () => this.firstFlight(), { passive: true });
      this.on(document, 'visibilitychange', () => (document.hidden ? this.stop() : this.start()));
      if (finePointer.matches) this.on(root, 'pointermove', (e) => this.onPointer(e), { passive: true });
      this.on(root, 'pointerleave', () => { this.tmx = 0; this.tmy = 0; });

      this.observe(root, ([entry]) => {
        this.visible = entry.isIntersecting;
        root.classList.toggle('is-idle', !this.visible);
        this.start();
      }, { rootMargin: '80px 0px' });

      this.watchGrid();
      ready.then(() => this.reveal());
    }

    /* ---------- loading curtain: reveal once styles, font and ghost are in (max 1.8 s) ---------- */
    whenReady() {
      const root = this.root;
      const css = new Promise((resolve) => {
        const check = () => (getComputedStyle(root).getPropertyValue('--ink').trim() ? resolve() : requestAnimationFrame(check));
        check();
      });
      const font = document.fonts && document.fonts.load
        ? document.fonts.load('900 1em "RR Commissioner"', 'HALLOWEEN ΕΩΣ').catch(() => {})
        : Promise.resolve();
      const ghost = root.querySelector('.rrhw__ghost img');
      const img = ghost && ghost.decode ? ghost.decode().catch(() => {}) : Promise.resolve();
      const timeout = new Promise((resolve) => setTimeout(resolve, 1800));
      return Promise.race([Promise.all([css, font, img]), timeout]);
    }

    reveal() {
      if (this.ready) return;
      this.ready = true;
      this.root.classList.remove('is-loading');
      this.root.classList.add('is-ready');
      this.startedAt = performance.now();
      this.start();
    }

    on(target, type, fn, opts) {
      target.addEventListener(type, fn, opts);
      this.cleanups.push(() => target.removeEventListener(type, fn, opts));
    }

    observe(el, fn, opts) {
      const io = new IntersectionObserver(fn, opts);
      io.observe(el);
      this.observers.push(io);
      return io;
    }

    destroy() {
      this.stop();
      clearTimeout(this.countTimer);
      this.observers.forEach((io) => io.disconnect());
      this.gridMo && this.gridMo.disconnect();
      this.hand && this.hand.ro && this.hand.ro.disconnect();
      this.cleanups.forEach((fn) => fn());
      this.fly && this.fly.remove();
      this.hand && this.hand.el.remove();
    }

    setOffset() {
      const top = this.root.getBoundingClientRect().top + window.scrollY;
      this.root.style.setProperty('--rrhw-offset', `${Math.max(0, Math.round(top))}px`);
    }

    /* ---------- countdown ---------- */
    initCountdown() {
      const ends = Date.parse(this.root.dataset.ends || '');
      const box = this.root.querySelector('[data-count]');
      if (!box) return;
      if (Number.isNaN(ends)) { box.hidden = true; return; }
      const units = {};
      box.querySelectorAll('[data-unit]').forEach((el) => { units[el.dataset.unit] = el; });
      const tick = () => {
        const left = ends - Date.now();
        if (left <= 0) { this.root.classList.add('is-ended'); return; }
        const s = Math.floor(left / 1000);
        units.d && (units.d.textContent = pad(Math.floor(s / 86400)));
        units.h && (units.h.textContent = pad(Math.floor((s % 86400) / 3600)));
        units.m && (units.m.textContent = pad(Math.floor((s % 3600) / 60)));
        units.s && (units.s.textContent = pad(s % 60));
        this.countTimer = setTimeout(tick, 1000 - (Date.now() % 1000) + 5);
      };
      tick();
    }

    initCta() {
      const cta = this.root.querySelector('[data-cta]');
      if (!cta) return;
      cta.addEventListener('click', (e) => {
        const target = document.getElementById((cta.getAttribute('href') || '').slice(1));
        if (!target) return;
        e.preventDefault();
        target.scrollIntoView({ behavior: this.reduced ? 'auto' : 'smooth', block: 'start' });
      });
    }

    /* ---------- spiders ---------- */
    initSpiders() {
      this.spiders = [...this.root.querySelectorAll('[data-spider]')].map((el, i) => {
        const body = el.querySelector('.rrhw-spider__body');
        const s = {
          el, body,
          thread: el.querySelector('.rrhw-spider__thread'),
          size: parseFloat(getComputedStyle(el).getPropertyValue('--size')) || 24,
          base: 0, len: 0, vlen: 0,
          th: (Math.random() - 0.5) * 0.3, om: 0,
          dropAt: 520 + i * 95,
          yankUntil: 0,
          nextGust: 1500 + Math.random() * 4000,
          dir: i % 2 ? -1 : 1,
        };
        body && body.addEventListener('pointerdown', (e) => { e.preventDefault(); this.yank(s, 900); });
        return s;
      });
    }

    readSpiderLengths() {
      (this.spiders || []).forEach((s) => {
        s.base = parseFloat(getComputedStyle(s.el).getPropertyValue('--l')) || 0;
        s.active = s.el.offsetParent !== null && s.base > 0;
      });
    }

    yank(s, ms) {
      s.yankUntil = performance.now() + ms;
      s.om += (Math.random() - 0.5) * 2.2;
    }

    stepSpiders(dt, now) {
      const elapsed = now - this.startedAt;
      const reel = 1 - 0.82 * clamp(this.p, 0, 1);
      for (const s of this.spiders) {
        if (!s.active) continue;
        let target = elapsed < s.dropAt ? 0 : s.base * reel;
        if (now < s.yankUntil) target = 4;
        const k = now < s.yankUntil ? 140 : 46;
        const c = 2 * Math.sqrt(k) * 0.42;
        s.vlen += (k * (target - s.len) - c * s.vlen) * dt;
        s.len += s.vlen * dt;
        if (s.len < 0) { s.len = 0; s.vlen = 0; }
        const L = Math.max(s.len, 6) + s.size * 0.5;
        let acc = -(900 / L) * Math.sin(s.th) - 0.55 * s.om;
        acc += clamp(this.scrollVel, -3000, 3000) * 0.00042 * s.dir;
        acc += -s.vlen * 0.004 * Math.sin(s.th * 3 + 1);
        s.nextGust -= dt * 1000;
        if (s.nextGust <= 0) { s.om += (Math.random() - 0.5) * 0.9; s.nextGust = 2200 + Math.random() * 4200; }
        s.om += acc * dt;
        s.th = clamp(s.th + s.om * dt, -0.75, 0.75);

        s.el.style.transform = `rotate(${s.th.toFixed(4)}rad)`;
        s.thread.style.transform = `scaleY(${(s.len / 100).toFixed(4)})`;
        s.body.style.transform = `translate3d(0, ${(s.len - s.size * 0.2).toFixed(2)}px, 0) rotate(${(-s.om * 0.06).toFixed(3)}rad)`;
      }
    }

    /* ---------- ghost ---------- */
    initGhost() {
      const ghost = this.root.querySelector('[data-ghost]');
      if (!ghost) return;
      ghost.addEventListener('click', () => {
        ghost.classList.remove('is-boo');
        void ghost.offsetWidth;
        ghost.classList.add('is-boo');
        const word = document.createElement('span');
        word.className = 'rrhw__boo';
        word.textContent = 'BOO!';
        ghost.appendChild(word);
        word.addEventListener('animationend', () => word.remove());
        (this.spiders || []).forEach((s, i) => setTimeout(() => this.yank(s, 750), 60 + i * 40));
      });
      ghost.addEventListener('animationend', (e) => { if (e.target === ghost) ghost.classList.remove('is-boo'); });
    }

    /* ---------- witch: page-level flights ---------- */
    initFly() {
      const fly = this.section.querySelector('[data-fly]');
      if (!fly) return;
      document.body.appendChild(fly); // escape the section's stacking context
      this.fly = fly;
      this.witch = fly.querySelector('[data-witch]');
      this.canvas = fly.querySelector('[data-trail]');
      this.ctx = this.canvas.getContext('2d');
      this.sprites = TRAIL_COLORS.map(makeSprite);
      const img = this.witch.querySelector('img');
      this.witchReady = img && img.decode ? img.decode().catch(() => {}) : Promise.resolve();
    }

    headerBottom() {
      const header = document.querySelector('.shopify-section--header, header');
      const b = header ? header.getBoundingClientRect().bottom : 0;
      return clamp(b, 0, window.innerHeight * 0.4);
    }

    firstFlight() {
      if (!this.ready || this.flownOut || !this.visible || !this.witch) return;
      this.flownOut = true;
      const moon = this.root.querySelector('.rrhw__moon');
      const mr = (moon || this.root).getBoundingClientRect();
      // she flies in the sky, fixed to the screen, always fully below the sticky header
      this.launch(-1, () => {
        const wh = this.witch.offsetHeight;
        const top = this.headerBottom() + 10;
        const y = clamp(mr.top + mr.height * 0.42 - wh * 0.7, top, Math.max(top, window.innerHeight * 0.55 - wh));
        return { y0: y, y1: y, fixed: true };
      });
    }

    returnFlight() {
      if (this.flownBack || !this.witch) return;
      this.flownBack = true;
      // second pass: across the screen itself, whatever the scroll position, below the sticky header
      this.launch(1, () => {
        const wh = this.witch.offsetHeight;
        const top = this.headerBottom() + 10;
        const y0 = clamp(window.innerHeight * 0.36 - wh * 0.5, top, Math.max(top, window.innerHeight - wh * 1.6));
        return { y0, y1: y0 + Math.min(60, window.innerHeight * 0.06), fixed: true };
      });
    }

    launch(dir, pathFn) {
      if (this.flight) { this.queued = { dir, pathFn }; return; }
      if (!this.witchDecoded) {
        // wait until the image is decoded, so her first frame never stalls the page
        this.witchReady.then(() => { this.witchDecoded = true; this.launch(dir, pathFn); });
        return;
      }
      const path = pathFn();
      if (!path) return;
      const vw = document.documentElement.clientWidth;
      this.witch.classList.toggle('is-back', dir > 0);
      const ww = this.witch.offsetWidth;
      this.flight = {
        dir, t0: performance.now(),
        dur: vw < 700 ? 3300 : 4600,
        x0: dir < 0 ? vw + 24 : -ww - 40,
        x1: dir < 0 ? -ww - 80 : vw + 40,
        y0: path.y0, y1: path.y1, fixed: !!path.fixed,
        amp: Math.min(36, window.innerHeight * 0.045),
        ww, wh: this.witch.offsetHeight,
      };
      // park her off-screen at the start point before the layer becomes visible (no one-frame flash)
      const sy = path.fixed ? 0 : window.scrollY;
      this.witch.style.transform = `translate3d(${this.flight.x0}px, ${path.y0 - sy}px, 0)`;
      this.fly.classList.add('is-flying');
      this.start();
    }

    stepFly(now, dt) {
      if (!this.fly) return false;
      const f = this.flight;
      if (!f && !this.particles.length) return false;
      const scrollY = window.scrollY;
      const sy = f && f.fixed ? 0 : scrollY;
      if (f) {
        const k = clamp((now - f.t0) / f.dur, 0, 1);
        const e = k < 0.5 ? 2 * k * k * 0.35 + k * 0.65 : 1 - (2 * (1 - k) * (1 - k) * 0.35 + (1 - k) * 0.65);
        const x = f.x0 + (f.x1 - f.x0) * e;
        const wave = Math.sin(k * Math.PI * 2.1);
        const yPage = f.y0 + (f.y1 - f.y0) * e + f.amp * wave - Math.sin(k * Math.PI) * f.amp * 0.8;
        const slope = Math.cos(k * Math.PI * 2.1) * f.amp * Math.PI * 2.1 / Math.abs(f.x1 - f.x0);
        const rot = clamp(slope * 57 * f.dir, -10, 10);
        this.witch.style.transform = `translate3d(${x.toFixed(1)}px, ${(yPage - sy).toFixed(1)}px, 0) rotate(${rot.toFixed(2)}deg)`;
        // sparks from the broom bristles: right end when flying left, left end when flying right
        const tailX = x + f.ww * (f.dir < 0 ? 0.96 : 0.04);
        const tailY = yPage + f.wh * 0.78;
        const n = this.vw < 700 ? 3 : 5;
        for (let i = 0; i < n; i++) {
          this.particles.push({
            x: tailX + (Math.random() - 0.5) * 10,
            y: tailY + (Math.random() - 0.5) * 12,
            vx: -f.dir * (40 + Math.random() * 150),
            vy: -30 + Math.random() * 70,
            life: 0, max: 0.6 + Math.random() * 0.8,
            r: 2 + Math.random() * 4.5,
            s: this.sprites[(Math.random() * this.sprites.length) | 0],
            fixed: f.fixed,
          });
        }
        if (k >= 1) {
          this.flight = null;
          if (this.queued) {
            const q = this.queued;
            this.queued = null;
            setTimeout(() => this.launch(q.dir, q.pathFn), 350);
          }
        }
      }
      const ctx = this.ctx;
      ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
      ctx.clearRect(0, 0, this.vw, this.vh);
      if (this.particles.length) {
        ctx.globalCompositeOperation = 'lighter';
        const keep = [];
        for (const p of this.particles) {
          p.life += dt;
          if (p.life >= p.max) continue;
          p.vy += 40 * dt;
          p.vx *= 0.985;
          p.x += p.vx * dt;
          p.y += p.vy * dt;
          const a = 1 - p.life / p.max;
          ctx.globalAlpha = a;
          const r = p.r * (0.6 + a * 0.6);
          ctx.drawImage(p.s, p.x - r, p.y - (p.fixed ? 0 : scrollY) - r, r * 2, r * 2);
          keep.push(p);
        }
        ctx.globalAlpha = 1;
        this.particles = keep.length > 600 ? keep.slice(-600) : keep;
      }
      const busy = !!this.flight || this.particles.length > 0;
      if (!busy) this.fly.classList.remove('is-flying');
      return busy;
    }

    /* ---------- return flight: once the shopper has scrolled past the first products ---------- */
    watchBackFlight() {
      const grid = mainGrid();
      const cards = grid ? [...grid.querySelectorAll('.product-card-container')] : [];
      if (!cards.length || this.flownBack) return;
      // the first card of the second row (on a phone: after the first 2 products)
      const firstTop = cards[0].getBoundingClientRect().top;
      const cols = Math.max(1, cards.filter((c) => Math.abs(c.getBoundingClientRect().top - firstTop) < 4).length);
      const target = cards[Math.min(cols, cards.length - 1)];
      if (this.backIo) this.backIo.disconnect();
      this.backIo = this.observe(target, ([en]) => {
        if (!en.isIntersecting || !this.flownOut) return;
        this.backIo.disconnect();
        setTimeout(() => this.returnFlight(), 450);
      }, { rootMargin: '0px 0px -45% 0px' });
    }

    watchGrid() {
      // filtering, sorting and paging re-render the grid: decorate again
      const grid = mainGrid();
      const host = grid && grid.closest('.shopify-section');
      if (!host) return;
      let t = 0;
      this.gridMo = new MutationObserver((records) => {
        if (records.every((r) => [...r.addedNodes].every((n) => n.nodeType !== 1 || n.matches?.('.rrhw-hand, .rrhw-hand-track')))) return;
        clearTimeout(t);
        t = setTimeout(() => { this.watchBackFlight(); this.placeHand(); }, 250);
      });
      this.gridMo.observe(host, { childList: true, subtree: true });
    }

    /* ---------- the walking hand ---------- */
    initHand() {
      const src = this.root.dataset.hand;
      const still = this.root.dataset.handStill;
      if (!src) return;
      const el = document.createElement('span');
      el.className = 'rrhw-hand is-still';
      el.setAttribute('aria-hidden', 'true');
      el.innerHTML = '<img class="rrhw-hand__walk" alt="" decoding="async"><img class="rrhw-hand__still" alt="" decoding="async">';
      this.hand = { el, src, still, s: 0, pauseUntil: 0, loaded: false, visible: false, host: null };
      this.placeHand();
    }

    placeHand() {
      const h = this.hand;
      if (!h) return;
      let host = document.querySelector('.collection nav.pagination');
      let track = false;
      if (!host) {
        const main = document.querySelector('.collection .collection__main');
        if (!main) return;
        host = main.querySelector('.rrhw-hand-track');
        if (!host) {
          host = document.createElement('div');
          host.className = 'rrhw-hand-track';
          host.setAttribute('aria-hidden', 'true');
          host.style.cssText = 'height:2px;width:min(80%,520px);margin:76px auto 6px;';
          main.appendChild(host);
        }
        track = true;
      }
      if (h.host === host && host.contains(h.el)) return;
      h.host = host;
      h.track = track;
      h.s = 0;
      host.classList.add('rrhw-hand-host');
      host.appendChild(h.el);
      const size = () => { h.w = host.offsetWidth; h.ht = host.offsetHeight; h.S = h.el.offsetWidth; };
      size();
      if (h.ro) h.ro.disconnect();
      if (window.ResizeObserver) { h.ro = new ResizeObserver(size); h.ro.observe(host); h.ro.observe(h.el); }
      if (h.io) h.io.disconnect();
      h.io = this.observe(host, ([en]) => {
        h.visible = en.isIntersecting;
        if (h.visible && !h.loaded) {
          h.loaded = true;
          const [walk, stillImg] = h.el.querySelectorAll('img');
          walk.src = h.src;
          stillImg.src = h.still;
          h.el.classList.remove('is-still');
        }
        this.start();
      }, { rootMargin: '200px 0px' });
    }

    stepHand(now, dt) {
      const h = this.hand;
      if (!h || !h.visible || !h.host || !h.loaded) return false;
      if (now < h.pauseUntil) return true;
      h.el.classList.remove('is-still');

      const w = h.w;
      const ht = h.track ? 0 : h.ht;
      const S = h.S;
      const g = 6;
      const speed = w < 260 ? 30 : 38;
      const pivot = 0.5 * speed; // a corner takes as long as half a second of walking

      let x, y, rot, flip = false;
      if (h.track) {
        // no page selector: pace back and forth along the end of the grid
        const span = Math.max(1, w - S);
        h.s = (h.s + speed * dt) % (span * 2);
        const fwd = h.s < span;
        x = (fwd ? h.s : span * 2 - h.s) + S / 2;
        y = 0; rot = 0; flip = !fwd;
      } else {
        const W = w + g * 2, H = ht + g * 2;
        const segs = [W, pivot, H, pivot, W, pivot, H, pivot];
        const total = segs.reduce((a, b) => a + b, 0);
        const before = h.s;
        h.s = (h.s + speed * dt) % total;
        // one short rest per lap, on the top edge
        const restAt = W * 0.62;
        if (before < restAt && h.s >= restAt) {
          h.pauseUntil = now + 1300;
          h.el.classList.add('is-still');
        }
        let s = h.s, i = 0;
        while (i < 7 && s > segs[i]) { s -= segs[i]; i++; }
        const t = clamp(s / segs[i], 0, 1);
        const L = -g, T = -g, R = w + g, B = ht + g;
        switch (i) {
          case 0: x = L + W * t; y = T; rot = 0; break;
          case 1: x = R; y = T; rot = 90 * t; break;
          case 2: x = R; y = T + H * t; rot = 90; break;
          case 3: x = R; y = B; rot = 90 + 90 * t; break;
          case 4: x = R - W * t; y = B; rot = 180; break;
          case 5: x = L; y = B; rot = 180 + 90 * t; break;
          case 6: x = L; y = B - H * t; rot = 270; break;
          default: x = L; y = T; rot = 270 + 90 * t;
        }
      }
      h.el.classList.toggle('is-flipped', flip);
      h.el.style.transformOrigin = '50% 100%';
      h.el.style.transform = `translate3d(${(x - S / 2).toFixed(1)}px, ${(y - S).toFixed(1)}px, 0) rotate(${rot.toFixed(1)}deg)`;
      return true;
    }

    /* ---------- pointer, scroll, progress ---------- */
    measure() {
      const r = this.root.getBoundingClientRect();
      this.top = r.top + window.scrollY;
      this.h = this.root.clientHeight;
      this.vw = document.documentElement.clientWidth;
      this.vh = window.innerHeight;
      this.setOffset();
      if (this.canvas) {
        this.dpr = 1;
        this.canvas.width = Math.round(this.vw * this.dpr);
        this.canvas.height = Math.round(this.vh * this.dpr);
      }
      this.readSpiderLengths();
      this.updateProgress(true);
    }

    onScroll() {
      const y = window.scrollY;
      this.scrollVel = (y - this.lastScrollY) * 60;
      this.lastScrollY = y;
      // ignore the browser's own scroll restoration right after load
      if (y > 2 && performance.now() - this.startedAt > 700) this.firstFlight();
      this.start();
    }

    onPointer(e) {
      const r = this.root.getBoundingClientRect();
      this.tmx = clamp(((e.clientX - r.left) / r.width) * 2 - 1, -1, 1);
      this.tmy = clamp(((e.clientY - r.top) / r.height) * 2 - 1, -1, 1);
      this.start();
    }

    updateProgress(force) {
      const p = clamp((window.scrollY - this.top + 80) / Math.max(1, this.h), 0, 1.2);
      if (force || Math.abs(p - this.p) > 0.0008) {
        this.p = p;
        this.root.style.setProperty('--p', p.toFixed(4));
      }
    }

    /* ---------- one loop for everything; it sleeps when nothing is on screen ---------- */
    start() {
      if (!this.ready || this.raf || this.reduced || document.hidden) return;
      this.last = performance.now();
      this.raf = requestAnimationFrame((t) => this.frame(t));
    }

    stop() {
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = 0;
    }

    frame(now) {
      const dt = Math.min(0.033, (now - this.last) / 1000);
      this.last = now;
      this.scrollVel *= 0.9;
      let busy = false;

      if (this.visible) {
        busy = true;
        this.updateProgress();
        this.mx += (this.tmx - this.mx) * Math.min(1, dt * 4);
        this.my += (this.tmy - this.my) * Math.min(1, dt * 4);
        if (Math.abs(this.mx) > 0.0005 || Math.abs(this.my) > 0.0005 || this.tmx || this.tmy) {
          this.root.style.setProperty('--mx', this.mx.toFixed(4));
          this.root.style.setProperty('--my', this.my.toFixed(4));
        }
        if (this.spiders) this.stepSpiders(dt, now);
      }
      if (this.stepFly(now, dt)) busy = true;
      if (this.stepHand(now, dt)) busy = true;

      this.raf = busy ? requestAnimationFrame((t) => this.frame(t)) : 0;
    }
  }

  const init = (root) => {
    if (!root || root.__rrhw) return;
    root.__rrhw = new HalloweenPage(root);
  };
  const boot = () => document.querySelectorAll('[data-rrhw]').forEach(init);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  document.addEventListener('shopify:section:load', (e) => init(e.target.querySelector('[data-rrhw]')));
  document.addEventListener('shopify:section:unload', (e) => {
    const root = e.target.querySelector('[data-rrhw]');
    if (root && root.__rrhw) { root.__rrhw.destroy(); root.__rrhw = null; }
  });
})();
